import * as React from 'react';
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { API } from '../../../services/amplifyCompat';
import { LogActionContainer } from '../../../views/LogActionContainer/LogActionContainer';
import { Button } from '../../../components/Button/Button';
import { getLogActionPath } from '../../../constants/log-action-paths';
import { useGetSingleAction } from '../../../hooks/use-get-single-action';
import { useGetActionQuiz } from '../../../hooks/use-get-action-quiz.hook';
import { useGetUserAction } from '../../../hooks/use-get-user-action';
import { updateSubmittedAction } from '../../../graphql/mutations';
import { ACTION_POINTS_VALUES } from '../../../constants/action-points-values';
import theme from '../../../themes';
import useTranslation from '../../../components/customHooks/translations';

const { BONUS_FULL, BONUS_PARTIAL } = ACTION_POINTS_VALUES;

/** Normalize correct_answers which may be a single answer or newline-joined multi-answers */
const getCorrectAnswers = (quizAnswer) => {
  if (!quizAnswer) return [];
  return String(quizAnswer)
    .split('\n')
    .map((a) => a.trim())
    .filter(Boolean);
};

const isAnswerCorrect = (answer, quizAnswer) => {
  const correct = getCorrectAnswers(quizAnswer);
  return correct.includes(answer);
};

export const BonusQuiz = () => {
  const navigate = useNavigate();
  const { actionId, userActionId } = useParams();
  const { actionStyles } = useGetSingleAction();
  const { userAction, loading: loadingUserAction } = useGetUserAction();
  const {
    actionQuiz: quiz,
    loading: loadingQuiz,
    error: quizLoadError,
    isEmpty: quizEmpty,
  } = useGetActionQuiz();
  const { userActionValues, setUserActionValues } = useOutletContext();

  const [answer, setAnswer] = React.useState('');
  const [status, setStatus] = React.useState(null);
  const [firstAnswer, setFirstAnswer] = React.useState(true);

  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [alreadyAnswered, setAlreadyAnswered] = React.useState(false);
  const submittingRef = React.useRef(false);
  const translation = useTranslation();

  // Block re-answering if quiz was already completed (prevents farming via browser Back)
  React.useEffect(() => {
    if (userAction?.quizAnswered) {
      setAlreadyAnswered(true);
      setStatus('correct');
      setSuccess(true);
    }
  }, [userAction?.quizAnswered]);

  const CorrectMessage = ({ firstAnswer }) => (
    <Typography variant="p" component="p" mt="1.5rem">
      <strong style={{ color: '#0D9488' }}>{translation.correct}</strong>
      {translation.formatString(
        translation['logActionPage'].greatJob,
        firstAnswer ? BONUS_FULL : BONUS_PARTIAL
      )}
    </Typography>
  );

  const IncorrectMessage = () => (
    <Typography variant="p" component="p" mt="1.5rem">
      <strong style={{ color: '#EB5757' }}>{translation.incorrect}</strong>
      {translation.formatString(
        translation['logActionPage'].tryAgain,
        BONUS_PARTIAL
      )}
    </Typography>
  );

  const onSubmitForm = (e) => {
    e.preventDefault();

    if (alreadyAnswered || success) {
      navigate(getLogActionPath('SHARE_SOCIAL', actionId, userActionId));
      return;
    }

    if (status === 'correct') {
      navigate(getLogActionPath('SHARE_SOCIAL', actionId, userActionId));
    } else {
      if (!answer) return;
      setStatus(isAnswerCorrect(answer, quiz?.answer) ? 'correct' : 'incorrect');
    }
  };

  const onChangeAnswer = (e) => {
    if (alreadyAnswered || success) return;
    setStatus(null);
    setAnswer(e.target.value);
  };

  React.useEffect(() => {
    const updateUserAction = async () => {
      if (submittingRef.current) return;
      if (!userAction?.id || Number.isNaN(Number(userAction?.id))) {
        setError(true);
        setSuccess(false);
        return;
      }

      submittingRef.current = true;
      setLoading(true);
      setError(null);

      const pointsEarned = firstAnswer
        ? (userAction?.pointsEarned || 0) + BONUS_FULL
        : (userAction?.pointsEarned || 0) + BONUS_PARTIAL;

      try {
        const variables = {
          sa_id: userAction.id,
          user_id: userAction.userId,
          action_id: parseInt(actionId, 10),
          quiz_id: quiz?.id,
          g_co2_saved: userAction.co2Saved,
          date_of_action: userAction.dateOfAction,
          is_validated: userAction.isValidated,
          quiz_answered: true,
          first_quiz_answer_correct: firstAnswer,
          points_earned: pointsEarned,
          answer,
        };

        await API.graphql({
          query: updateSubmittedAction,
          variables,
        });

        setUserActionValues((prev) => ({ ...prev, pointsEarned }));
        setSuccess(true);
        setLoading(false);
      } catch (e) {
        console.log('Error: ', e);
        setSuccess(false);
        setError(true);
        setLoading(false);
        submittingRef.current = false;
        // Reset status so user can retry
        setStatus(null);
      }
    };

    if (
      !alreadyAnswered &&
      !loading &&
      success === null &&
      status === 'correct' &&
      userAction?.id
    ) {
      updateUserAction();
    }
  }, [
    loading,
    success,
    status,
    userAction,
    firstAnswer,
    quiz,
    actionId,
    setUserActionValues,
    answer,
    alreadyAnswered,
  ]);

  React.useEffect(() => {
    if (status === 'incorrect') {
      setFirstAnswer(false);
    }
  }, [status]);

  // Do NOT auto-select the first option — require an explicit choice

  if (loadingQuiz || loadingUserAction) {
    return (
      <LogActionContainer step={4} buttons={null}>
        <CircularProgress aria-label={translation.logActionPage?.loading || 'Loading'} />
      </LogActionContainer>
    );
  }

  if (quizLoadError || quizEmpty || !quiz?.options?.length) {
    return (
      <LogActionContainer
        step={4}
        buttons={
          <Button
            component={Link}
            to={getLogActionPath('SHARE_SOCIAL', actionId, userActionId)}
            variant="contained"
          >
            {translation.continue}
          </Button>
        }
      >
        {quizLoadError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {translation.logActionPage?.quizLoadError ||
              'Could not load the bonus quiz. You can continue without it.'}
          </Alert>
        ) : (
          <Typography variant="p" component="p" mt="1.5rem">
            {translation.logActionPage?.noQuizAvailable ||
              'No quiz is available for this action.'}
          </Typography>
        )}
      </LogActionContainer>
    );
  }

  return (
    <LogActionContainer
      step={4}
      buttons={
        <>
          <Button
            component={Link}
            to={getLogActionPath('SHARE_SOCIAL', actionId, userActionId)}
            variant="outlined"
            sx={{
              border: '1px solid #E2E8F0',
              color: '#0F172A',
              '&:hover': { borderColor: '#0D9488', color: '#0D9488' },
              [theme.breakpoints.down('sm')]: { order: '2' },
            }}
          >
            {translation.skip}
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="form-BonusQuiz"
            disabled={loading || (!answer && !alreadyAnswered && !success)}
          >
            {status === 'correct' || alreadyAnswered || success
              ? translation.continue
              : translation.submit}
          </Button>
        </>
      }
    >
      <Typography
        variant="h1"
        sx={{
          color: actionStyles?.color,
        }}
      >
        {translation.logActionStep5}!
      </Typography>
      <Typography
        variant="p"
        component="p"
        sx={{ color: '#0D9488', mt: '2rem' }}
      >
        {translation.extraPoints}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {translation.logActionPage?.quizSaveError ||
            'Could not save quiz answer. Please try again.'}
        </Alert>
      )}
      {alreadyAnswered && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {translation.logActionPage?.quizAlreadyAnswered ||
            'You have already completed this quiz.'}
        </Alert>
      )}
      <Box component="form" id="form-BonusQuiz" onSubmit={onSubmitForm}>
        <Box
          component="fieldset"
          sx={{ margin: 0, padding: 0, outline: 'none', border: 'none' }}
          disabled={alreadyAnswered || success}
        >
          <Typography
            variant="p"
            component="legend"
            id="bonus-quiz-legend"
            sx={{ padding: 0, margin: '1rem 0' }}
          >
            {quiz?.question}
          </Typography>
          <RadioGroup
            aria-labelledby="bonus-quiz-legend"
            aria-describedby="bonus-quiz-status"
            name="bonus-quiz"
            value={answer}
            onChange={onChangeAnswer}
          >
            {quiz.options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio disabled={alreadyAnswered || !!success} />}
                label={option}
                disabled={alreadyAnswered || !!success}
              />
            ))}
          </RadioGroup>
          <Box aria-live="polite" id="bonus-quiz-status">
            {status === 'correct' && success ? (
              <CorrectMessage firstAnswer={firstAnswer} />
            ) : null}
            {status === 'incorrect' ? <IncorrectMessage /> : null}
          </Box>
        </Box>
      </Box>
    </LogActionContainer>
  );
};
